import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

export const bankMovementKeys = {
  all: ['bankMovements'],
  lists: () => [...bankMovementKeys.all, 'list'],
  details: () => [...bankMovementKeys.all, 'detail'],
  detail: (id) => [...bankMovementKeys.details(), id],
  byAccount: (accountId) => [...bankMovementKeys.all, 'byAccount', accountId],
  byPaymentOrder: (paymentOrderId) => [...bankMovementKeys.all, 'byPaymentOrder', paymentOrderId],
  byLegalEntity: (legalEntityId) => [...bankMovementKeys.all, 'byLegalEntity', legalEntityId],
  reconciled: (isReconciled) => [...bankMovementKeys.all, 'reconciled', isReconciled],
  byVoucher: (voucherNumber) => [...bankMovementKeys.all, 'byVoucher', voucherNumber],
  byDateRange: (filters) => [...bankMovementKeys.all, 'byDateRange', filters],
  balance: (accountId, date, accountKind = 'bank') => [...bankMovementKeys.all, 'balance', accountId, date, accountKind],
  exchangeChecks: (filters) => [...bankMovementKeys.all, 'exchangeChecks', filters],
};

const invalidateExchangeCheckQueries = (queryClient) => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === 'bankMovements' &&
      query.queryKey[1] === 'exchangeChecks',
  });
};

/**
 * Hook para obtener todos los movimientos bancarios activos
 */
export const useBankMovements = () => {
  return useQuery({
    queryKey: bankMovementKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('bank_movements');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener un movimiento bancario específico
 * @param {string} id - UUID del movimiento bancario
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankMovement = (id, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`bank_movements/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener movimientos bancarios por cuenta
 * @param {string} accountId - UUID de la cuenta bancaria
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankMovementsByAccount = (accountId, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.byAccount(accountId),
    queryFn: async () => {
      const response = await apiClient.get(`bank_movements/by-account/${accountId}`);
      return response.data;
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener movimientos bancarios por orden de pago
 * @param {string} paymentOrderId - UUID de la orden de pago
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankMovementsByPaymentOrder = (paymentOrderId, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.byPaymentOrder(paymentOrderId),
    queryFn: async () => {
      const response = await apiClient.get(`bank_movements/by-payment-order/${paymentOrderId}`);
      return response.data;
    },
    enabled: !!paymentOrderId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener movimientos bancarios por entidad legal
 * @param {string} legalEntityId - UUID de la entidad legal
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankMovementsByLegalEntity = (legalEntityId, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.byLegalEntity(legalEntityId),
    queryFn: async () => {
      const response = await apiClient.get(`bank_movements/by-legal-entity/${legalEntityId}`);
      return response.data;
    },
    enabled: !!legalEntityId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener movimientos bancarios por estado de conciliación
 * @param {boolean} isReconciled - Estado de conciliación
 * @param {Object} options - Opciones adicionales para la query
 */
export const useReconciledBankMovements = (isReconciled = true, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.reconciled(isReconciled),
    queryFn: async () => {
      const response = await apiClient.get('bank_movements/reconciled', {
        params: { is_reconciled: isReconciled },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener movimientos bancarios por número de comprobante
 * @param {string} voucherNumber - Número de comprobante
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankMovementsByVoucher = (voucherNumber, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.byVoucher(voucherNumber),
    queryFn: async () => {
      const response = await apiClient.get(`bank_movements/by-voucher/${voucherNumber}`);
      return response.data;
    },
    enabled: !!voucherNumber,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener movimientos bancarios por rango de fechas
 * @param {Object} filters - Filtros con start_date, end_date y opcionalmente account_id
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankMovementsByDateRange = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.byDateRange(filters),
    queryFn: async () => {
      const response = await apiClient.get('bank_movements/by-date-range', {
        params: filters,
      });
      return response.data;
    },
    enabled: !!filters.start_date && !!filters.end_date,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener cheques de canje por rango de fechas
 * @param {Object} filters - Filtros con start_date, end_date y opcionalmente account_id
 * @param {Object} options - Opciones adicionales para la query
 */
export const useExchangeChecks = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.exchangeChecks(filters),
    queryFn: async () => {
      const response = await apiClient.get('bank_movements/exchange-checks', {
        params: filters,
      });
      return response.data;
    },
    enabled: !!filters.start_date && !!filters.end_date,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener el saldo de una cuenta bancaria en una fecha específica
 * @param {string} accountId - UUID de la cuenta bancaria
 * @param {string} date - Fecha hasta la cual calcular el saldo (YYYY-MM-DD)
 * @param {Object} options - Opciones adicionales para la query
 */
export const useAccountBalance = (accountId, date, accountKind = 'bank', options = {}) => {
  return useQuery({
    queryKey: bankMovementKeys.balance(accountId, date, accountKind),
    queryFn: async () => {
      const response = await apiClient.get(`bank_movements/balance/${accountId}`, {
        params: { date, account_kind: accountKind },
      });
      return response.data;
    },
    enabled: !!accountId && !!date,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear un nuevo movimiento bancario
 */
export const useCreateBankMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bankMovementData) => {
      const response = await apiClient.post('bank_movements', bankMovementData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      if (data.uuid_bank_account) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byAccount(data.uuid_bank_account),
        });
      }
      if (data.uuid_cash_account) {
        queryClient.invalidateQueries({ queryKey: ['supplierLedger'] });
      }
      if (data.uuid_payment_order) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byPaymentOrder(data.uuid_payment_order),
        });
      }
      if (data.uuid_legal_entity) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byLegalEntity(data.uuid_legal_entity),
        });
      }
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(true) });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(false) });
      if (data.uuid_bank_account) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_bank_account,
        });
      }
      if (data.uuid_cash_account) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_cash_account,
        });
      }
      invalidateExchangeCheckQueries(queryClient);
    },
    onError: (error) => {
      console.error('Error creating bank movement:', error);
    },
  });
};

/**
 * Hook para actualizar un movimiento bancario existente
 */
export const useUpdateBankMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`bank_movements/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(bankMovementKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      if (data.uuid_bank_account) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byAccount(data.uuid_bank_account),
        });
      }
      if (data.uuid_cash_account) {
        queryClient.invalidateQueries({ queryKey: ['supplierLedger'] });
      }
      if (data.uuid_payment_order) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byPaymentOrder(data.uuid_payment_order),
        });
      }
      if (data.uuid_legal_entity) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byLegalEntity(data.uuid_legal_entity),
        });
      }
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(true) });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(false) });
      if (data.uuid_bank_account) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_bank_account,
        });
      }
      if (data.uuid_cash_account) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_cash_account,
        });
      }
      invalidateExchangeCheckQueries(queryClient);
    },
    onError: (error) => {
      console.error('Error updating bank movement:', error);
    },
  });
};

/**
 * Hook para eliminar lógicamente un movimiento bancario
 */
export const useDeleteBankMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`bank_movements/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      queryClient.removeQueries({ queryKey: bankMovementKeys.detail(deletedId) });
      if (data.uuid_bank_account) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byAccount(data.uuid_bank_account),
        });
      }
      if (data.uuid_cash_account) {
        queryClient.invalidateQueries({ queryKey: ['supplierLedger'] });
      }
      if (data.uuid_payment_order) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byPaymentOrder(data.uuid_payment_order),
        });
      }
      if (data.uuid_legal_entity) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byLegalEntity(data.uuid_legal_entity),
        });
      }
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(true) });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(false) });
      if (data.uuid_bank_account) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_bank_account,
        });
      }
      if (data.uuid_cash_account) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_cash_account,
        });
      }
      invalidateExchangeCheckQueries(queryClient);
    },
    onError: (error) => {
      console.error('Error deleting bank movement:', error);
    },
  });
};

/**
 * Hook para cambiar el estado de conciliación de un movimiento bancario
 */
export const useToggleReconciliation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.patch(`bank_movements/${id}/toggle-reconciliation`);
      return response.data;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: bankMovementKeys.all });

      queryClient.setQueriesData(
        { queryKey: bankMovementKeys.all },
        (oldData) => {
          if (!oldData) return oldData;

          if (Array.isArray(oldData)) {
            return oldData.map((movement) =>
              movement.uuid === id
                ? { ...movement, is_reconciled: !movement.is_reconciled }
                : movement
            );
          }

          if (oldData.uuid === id) {
            return { ...oldData, is_reconciled: !oldData.is_reconciled };
          }

          return oldData;
        }
      );

    },
    onError: (error) => {
      console.error('Error toggling reconciliation status:', error);
      // En caso de error, invalidar para refrescar desde el servidor
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
    },
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'bankMovements' &&
          query.queryKey[1] === 'byDateRange',
      });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(true) });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.reconciled(false) });

      if (id) {
        queryClient.invalidateQueries({ queryKey: bankMovementKeys.detail(id) });
      }

      if (data?.uuid_bank_account) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byAccount(data.uuid_bank_account),
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_bank_account,
        });
      }
      if (data?.uuid_cash_account) {
        queryClient.invalidateQueries({ queryKey: ['supplierLedger'] });
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'bankMovements' &&
            query.queryKey[1] === 'balance' &&
            query.queryKey[2] === data.uuid_cash_account,
        });
      }

      if (data?.uuid_payment_order) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byPaymentOrder(data.uuid_payment_order),
        });
      }

      if (data?.uuid_legal_entity) {
        queryClient.invalidateQueries({
          queryKey: bankMovementKeys.byLegalEntity(data.uuid_legal_entity),
        });
      }
      invalidateExchangeCheckQueries(queryClient);
    },
  });
};
